import React, { useEffect, useState, useCallback, useMemo } from "react";
import { z } from "zod";
import { useForm, useFieldArray, Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { cn } from "@/lib/utils";
import { Settings, FileText, Users, ListTodo, CheckCircle2, ExternalLink } from "lucide-react";
import { UpsertFormRequest } from "@/services/bedrock";

import { SettingsSection } from "./sections/SettingsSection";
import { IntroSection } from "./sections/IntroSection";
import { ContactSection } from "./sections/ContactSection";
import { FieldsSection } from "./sections/FieldsSection";
import { OutroSection } from "./sections/OutroSection";

const formFieldSchema = z.object({
  id: z.union([z.number(), z.string()]).optional(),
  fieldKey: z
    .string()
    .min(1, "Field Key is required")
    .max(100, "Field Key must be less than 100 characters")
    .regex(/^[a-z0-9_]+$/, "Field Key must only contain lowercase letters, numbers, and underscores"),
  title: z.string().min(1, "Question Title is required").max(255, "Question Title must be less than 255 characters"),
  subtitle: z.string().nullable().optional(),
  fieldType: z.string().min(1, "Field Type is required"),
  required: z.boolean().default(false),
  position: z.number().optional(),
  fieldOptions: z.record(z.string(), z.any()).nullable().optional(),
  active: z.boolean().default(true),
});

const formSchema = z
  .object({
    displayName: z.string().min(1, "Form Name is required").max(255, "Form Name must be less than 255 characters"),
    active: z.boolean().default(true),
    fanIdentityCollection: z.enum(["hidden", "optional", "required"]).default("hidden"),
    allowAiPersonalization: z.boolean().default(true),
    contentPageThemeId: z.number().nullable().optional(),
    allowThemeChange: z.boolean().default(true),
    allowTranslations: z.boolean().default(false),
    supportedLanguages: z.array(z.enum(["en", "es"])).default(["en"]),

    introTitle: z.string().max(255, "Intro Title must be less than 255 characters").nullable().optional(),
    introText: z.string().nullable().optional(),

    formFields: z.array(formFieldSchema).default([]),

    outroTitle: z.string().max(255, "Outro Title must be less than 255 characters").nullable().optional(),
    outroText: z.string().nullable().optional(),
    outroCtaLabel: z.string().nullable().optional(),
    outroCtaType: z.string().nullable().optional(),
    outroCtaLink: z.string().nullable().optional(),
  })
  .refine(
    data => {
      const keys = data.formFields.map(f => f.fieldKey);
      return new Set(keys).size === keys.length;
    },
    {
      message: "Each field must have a unique Key",
      path: ["formFields"],
    }
  );

export type FormSchemaType = z.output<typeof formSchema>;

interface FormEditViewProps {
  id: string;
  initialData: UpsertFormRequest;
  onSubmit: (data: FormSchemaType) => void;
  onValidationError?: (errorMessage: string | null) => void;
}

function getFirstErrorMessage(errors: any): string | null {
  if (!errors || Object.keys(errors).length === 0) return null;

  // Top-level fields first for clear prioritization
  if (errors.displayName?.message) return errors.displayName.message as string;
  if (errors.introTitle?.message) return errors.introTitle.message as string;
  if (errors.outroTitle?.message) return errors.outroTitle.message as string;
  if (errors.outroCtaLabel?.message) return errors.outroCtaLabel.message as string;
  if (errors.outroCtaType?.message) return errors.outroCtaType.message as string;
  if (errors.outroCtaLink?.message) return errors.outroCtaLink.message as string;

  // Let's check formFields errors
  if (errors.formFields) {
    if (Array.isArray(errors.formFields)) {
      for (const fieldError of errors.formFields) {
        if (fieldError) {
          if (fieldError.title?.message) return fieldError.title.message as string;
          if (fieldError.fieldType?.message) return fieldError.fieldType.message as string;
        }
      }
    } else if (errors.formFields.message) {
      return errors.formFields.message as string;
    }
  }

  // Fallback to recursively finding a message string
  const findMessage = (obj: any): string | null => {
    if (!obj || typeof obj !== "object") return null;
    if (typeof obj.message === "string") return obj.message;
    for (const key of Object.keys(obj)) {
      const res = findMessage(obj[key]);
      if (res) return res;
    }
    return null;
  };

  return findMessage(errors);
}

export function FormEditView({ id, initialData, onSubmit, onValidationError }: FormEditViewProps) {
  const testUrl = (initialData as any).testRequest?.url;

  const [activeSection, setActiveSection] = useState("settings");
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  const sectionsList = useMemo(
    () => [
      { id: "section-settings", name: "settings", label: "Form Settings", icon: Settings },
      { id: "section-intro", name: "intro", label: "Intro Section", icon: FileText },
      { id: "section-contact", name: "contact", label: "Contact Collection", icon: Users },
      { id: "section-fields", name: "fields", label: "Form Fields", icon: ListTodo },
      { id: "section-outro", name: "outro", label: "Outro Section", icon: CheckCircle2 },
    ],
    []
  );

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const containerTop = container.getBoundingClientRect().top;
    let currentActive = "settings";

    for (const sec of sectionsList) {
      const el = document.getElementById(sec.id);
      if (el) {
        const rect = el.getBoundingClientRect();
        if (rect.top - containerTop <= 120) {
          currentActive = sec.name;
        }
      }
    }
    setActiveSection(currentActive);
  }, [sectionsList]);

  const scrollToSection = (sectionId: string) => {
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const form = useForm<FormSchemaType>({
    resolver: zodResolver(formSchema) as Resolver<FormSchemaType>,
    mode: "onTouched",
    defaultValues: {
      displayName: "New Feedback Form",
      active: true,
      fanIdentityCollection: "hidden",
      allowAiPersonalization: false,
      contentPageThemeId: null,
      allowThemeChange: true,
      allowTranslations: false,
      introTitle: "",
      introText: "",
      outroTitle: "",
      outroText: "",
      outroCtaLabel: "Share with a friend",
      outroCtaType: "share_player_link",
      outroCtaLink: null,
      formFields: [],
      ...initialData,
      supportedLanguages: initialData.supportedLanguages ?? ["en"],
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    getFieldState,
    trigger,
    formState: { errors },
  } = form;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "formFields",
  });

  const outroCtaLabel = watch("outroCtaLabel");
  const outroCtaType = watch("outroCtaType");
  const supportedLanguages = watch("supportedLanguages") || [];

  useEffect(() => {
    const hasMultiple = supportedLanguages.length > 1;
    setValue("allowTranslations", hasMultiple, { shouldDirty: true });
  }, [supportedLanguages, setValue]);

  useEffect(() => {
    reset({
      displayName: "New Feedback Form",
      active: true,
      fanIdentityCollection: "hidden",
      allowAiPersonalization: false,
      contentPageThemeId: null,
      allowThemeChange: true,
      allowTranslations: false,
      introTitle: "",
      introText: "",
      outroTitle: "",
      outroText: "",
      outroCtaLabel: "Share with a friend",
      outroCtaType: "share_player_link",
      outroCtaLink: null,
      formFields: [],
      ...initialData,
      supportedLanguages: initialData.supportedLanguages ?? ["en"],
    });
  }, [initialData, reset]);

  const onValidationErrorRef = React.useRef(onValidationError);
  useEffect(() => {
    onValidationErrorRef.current = onValidationError;
  });

  useEffect(() => {
    const errorMsg = getFirstErrorMessage(errors);
    onValidationErrorRef.current?.(errorMsg);
  }, [errors]);

  const addField = () => {
    const defaultType = "text";
    const otherCount = fields.filter(f => f.fieldType === defaultType).length;
    const key = otherCount === 0 ? defaultType : `${defaultType}_${otherCount + 1}`;

    append({
      title: "New Question",
      fieldType: defaultType,
      required: false,
      active: true,
      fieldKey: key,
      position: fields.length,
    });
  };

  const handleIncludeCtaCheckedChange = useCallback(
    (checked: boolean) => {
      if (!checked) {
        setValue("outroCtaLabel", null, { shouldDirty: true });
        setValue("outroCtaType", null, { shouldDirty: true });
        setValue("outroCtaLink", null, { shouldDirty: true });
        return;
      }
      setValue("outroCtaLabel", "Share with a friend", { shouldDirty: true });
      setValue("outroCtaType", "share_player_link", { shouldDirty: true });
      setValue("outroCtaLink", null, { shouldDirty: true });
    },
    [setValue]
  );

  return (
    <form
      id={id}
      onSubmit={handleSubmit(onSubmit)}
      className="animate-in fade-in duration-300 w-full h-full flex flex-col"
    >
      <div className="flex flex-row w-full divide-x divide-border flex-1 min-h-0">
        {/* Left panel: Table of Contents */}
        <div className="w-60 bg-slate-50/50 p-4 shrink-0 flex flex-col justify-between h-full overflow-y-auto">
          <div className="space-y-1">
            <div className="px-3 py-2 text-xs font-bold text-slate-400 uppercase tracking-wide">Form Sections</div>
            {sectionsList.map(sec => {
              const Icon = sec.icon;
              const isActive = activeSection === sec.name;
              return (
                <button
                  key={sec.name}
                  type="button"
                  onClick={() => scrollToSection(sec.id)}
                  className={cn(
                    "w-full text-left px-3 py-2 rounded-lg text-sm transition-all duration-200 flex items-center gap-2.5 font-sans font-medium",
                    isActive
                      ? "bg-primary/10 text-primary font-bold"
                      : "text-slate-600 hover:bg-slate-100/70 hover:text-slate-900"
                  )}
                >
                  <Icon className={cn("size-4 shrink-0", isActive ? "text-primary" : "text-slate-400")} />
                  <span>{sec.label}</span>
                </button>
              );
            })}
          </div>

          {testUrl && (
            <div className="mt-6 p-3 bg-white border border-slate-200 rounded-xl flex flex-col gap-1.5 shadow-sm select-none">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Test URL</span>
              <p className="text-[10px] text-slate-500 leading-normal">
                Use this link to test your form layout and response flows.
              </p>
              <a
                href={testUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline font-semibold mt-1"
              >
                <span>Open Test Form</span>
                <ExternalLink className="size-3.5" />
              </a>
            </div>
          )}
        </div>

        {/* Right panel: Scrollable Form Sections */}
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto p-6 pb-32 space-y-8 bg-slate-50/50"
        >
          {/* 1. Form Global Settings */}
          <SettingsSection
            control={control}
            initialThemeId={initialData?.contentPageThemeId ?? undefined}
            isSubmitting={form.formState.isSubmitting}
          />

          {/* 2. Intro Section */}
          <IntroSection control={control} />

          {/* 3. User Contact Collection Mode */}
          <ContactSection watch={watch} setValue={setValue} />

          {/* 4. Form Fields */}
          <FieldsSection
            control={control}
            fields={fields}
            addField={addField}
            remove={remove}
            errors={errors}
            setValue={setValue}
          />

          {/* 5. Outro Section */}
          <OutroSection
            control={control}
            errors={errors}
            outroCtaLabel={outroCtaLabel}
            outroCtaType={outroCtaType}
            handleIncludeCtaCheckedChange={handleIncludeCtaCheckedChange}
            setValue={setValue}
          />
        </div>
      </div>
    </form>
  );
}
